// @ts-strict-ignore
import React from 'react';
import { AppComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import { ModalProps } from 'antd/lib/modal';
import Descriptions from 'antd/lib/descriptions';
import Space from 'antd/lib/space';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { EditOutlined } from '@ant-design/icons';
import { CustomMessageConfigKey, CustomMessagesConfig } from '../../types';
import { displayCustomMessageTitle } from '../../helpers/displayHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { selectCustomMessagesConfig } from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { AppSettingsV2Updatable } from '../../actions/updatable';
import { globalMessage } from '../../utils/globalMessage';
import { tryUpdateAppSettingsV2 } from '../../actions/appSettings';
import { TokenizedTextEditModal } from '../TokenizedTextEditModal';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { antGrey } from '../styles/globalStyles';

export const customDescriptionForCustomMessageConfigKey: (key: CustomMessageConfigKey) => string = (key) => {
    switch (key) {
        case CustomMessageConfigKey.VCSReleaseBody:
            return 'The default string for the VCS release body is dynamically generated from the list of commits and associated tickets in the diff for the release.';
        case CustomMessageConfigKey.CherryPickPRTitle:
            return 'The default string for the cherry-pick PR title is dynamically generated from the code items that were cherry-picked as part of this work.';
        case CustomMessageConfigKey.CherryPickPRBody:
            return 'The default string for the cherry-pick PR body is dynamically generated from the list of commits that were cherry-picked as part of this work.';
        default:
            return null;
    }
};

interface CustomMessageModalProps extends ModalProps, AppComponentProps {
    customMessageConfigKey: CustomMessageConfigKey | null;
    customMessagesConfig: CustomMessagesConfig;
}

const CustomMessageModal: React.FC<CustomMessageModalProps> = ({
    customMessageConfigKey,
    customMessagesConfig,
    onCancel,
    appId,
    orgId,
    ...rest
}) => {
    const [message, setMessage] = React.useState('');

    const templateData = customMessagesConfig.customMessageTemplatesData[customMessageConfigKey];

    const availableTokens = templateData?.availableTokens ?? [];
    const defaultValue = templateData?.defaultValue ?? '';
    const maxLength = templateData?.maxCharCount;
    const isDefault = message === defaultValue;

    const applyChangesIsLoading = useIsLoading(
        AppSettingsV2Updatable.UpdateCustomMessagesTemplates,
        customMessageConfigKey,
    );
    const applyChangesError = useError(AppSettingsV2Updatable.UpdateCustomMessagesTemplates, customMessageConfigKey);
    const applyChangesSuccess = useUpdated(
        AppSettingsV2Updatable.UpdateCustomMessagesTemplates,
        customMessageConfigKey,
    );

    const dispatch = useDispatch();
    const updateCustomMessagesTemplates = () => {
        if (message === '' && message !== defaultValue) {
            globalMessage.error('Custom string cannot be empty');
            return;
        }

        if (customMessageConfigKey === CustomMessageConfigKey.CherryPickCommitMessage) {
            const commitMessageTokenCount = (message.match(/{commitMessage}/g) || []).length;
            if (commitMessageTokenCount !== 1) {
                globalMessage.error('{commitMessage} token is required');
                return;
            }
        }

        dispatch(
            tryUpdateAppSettingsV2(
                AppSettingsV2Updatable.UpdateCustomMessagesTemplates,
                {
                    orgId,
                    appId,
                    customMessageTemplates: [
                        {
                            customStringType: customMessageConfigKey,
                            customStringValue: isDefault ? null : message,
                        },
                    ],
                },
                customMessageConfigKey,
            ),
        );
    };

    React.useEffect(() => {
        if (applyChangesError) {
            globalMessage.error(applyChangesError);
        }
    }, [applyChangesError]);

    React.useEffect(() => {
        if (applyChangesSuccess) {
            globalMessage.success(
                `${displayCustomMessageTitle(customMessageConfigKey)} template updated successfully!`,
            );
            onCancel(null);
        }
    }, [applyChangesSuccess]);

    // after templateData is set, set the message to the customStringValue
    React.useEffect(() => {
        setMessage(templateData?.customStringValue ?? defaultValue);
    }, [templateData?.customStringValue, defaultValue]);

    if (!customMessageConfigKey) {
        return null;
    }

    return (
        <TokenizedTextEditModal
            onCancel={onCancel}
            applyChangesIsLoading={applyChangesIsLoading}
            onSubmit={updateCustomMessagesTemplates}
            availableTokens={availableTokens}
            maxLength={maxLength}
            titleText={`Edit custom string for: ${displayCustomMessageTitle(customMessageConfigKey)}`}
            text={message}
            description={customDescriptionForCustomMessageConfigKey(customMessageConfigKey)}
            setText={setMessage}
            defaultText={defaultValue}
            {...rest}
        />
    );
};

interface CustomMessagesSectionProps {
    description: string;
    customMessageConfigKeys: CustomMessageConfigKey[];
    customMessagesConfig: CustomMessagesConfig;
    onEditClicked: (key: CustomMessageConfigKey) => void;
}

const CustomMessagesSection: React.FC<CustomMessagesSectionProps> = ({
    description,
    customMessageConfigKeys,
    customMessagesConfig,
    onEditClicked,
}) => {
    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text style={{ fontSize: 15, fontWeight: 700, color: antGrey.grey9 }}>{description}</Text>
            <Descriptions
                size="middle"
                layout="horizontal"
                bordered
                column={1}
                style={{ marginBottom: 24 }}
                labelStyle={{ width: 300 }}
            >
                {customMessageConfigKeys.map((key) => {
                    const templateData = customMessagesConfig.customMessageTemplatesData[key];
                    // if there is no custom string value then we are using the default
                    const isDefault = !templateData?.customStringValue;
                    const customDescription = customDescriptionForCustomMessageConfigKey(key);
                    const shouldUseCustomDescription = isDefault && customDescription !== null;
                    const contents = templateData?.customStringValue ?? templateData?.defaultValue;

                    return (
                        <Descriptions.Item key={key} label={displayCustomMessageTitle(key)}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text italic={shouldUseCustomDescription} style={{ whiteSpace: 'pre-wrap' }}>
                                    {shouldUseCustomDescription ? customDescription : contents}
                                </Text>
                                <PermissionedActionButton
                                    actionType={RunwayUserAction.UAUpdateAppSettings}
                                    type="link"
                                    icon={<EditOutlined />}
                                    onClick={() => onEditClicked(key)}
                                >
                                    Edit
                                </PermissionedActionButton>
                            </div>
                        </Descriptions.Item>
                    );
                })}
            </Descriptions>
        </Space>
    );
};

type CustomMessagesSettingsProps = AppComponentProps;

export const CustomMessagesSettings: React.FC<CustomMessagesSettingsProps> = ({ appId, orgId }) => {
    const [currentCustomMessageKey, setCurrentCustomMessageKey] = React.useState<CustomMessageConfigKey | null>(null);
    const [showCustomMessageModal, setShowCustomMessageModal] = React.useState(false);

    const onEditClicked = (key: CustomMessageConfigKey) => {
        setCurrentCustomMessageKey(key);
        setShowCustomMessageModal(true);
    };

    const customMessagesConfig = useSelector((state: RootState) => selectCustomMessagesConfig(state, appId));

    return (
        <>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <CustomMessagesSection
                    onEditClicked={onEditClicked}
                    customMessagesConfig={customMessagesConfig}
                    description="Pull request generated when Runway bumps the version in code on your working branch or release branch"
                    customMessageConfigKeys={[
                        CustomMessageConfigKey.BumpVersionPRTitle,
                        CustomMessageConfigKey.BumpVersionPRBody,
                        CustomMessageConfigKey.BumpVersionCommitMessage,
                    ]}
                />
                <CustomMessagesSection
                    onEditClicked={onEditClicked}
                    customMessagesConfig={customMessagesConfig}
                    description="Pull request generated when Runway promotes code from your working branch to your main branch"
                    customMessageConfigKeys={[
                        CustomMessageConfigKey.PromoteCodePRTitle,
                        CustomMessageConfigKey.PromoteCodePRBody,
                    ]}
                />
                <CustomMessagesSection
                    onEditClicked={onEditClicked}
                    customMessagesConfig={customMessagesConfig}
                    description="Pull request generated when Runway merges the release branch back into the working branch (and any other existing open release branches)"
                    customMessageConfigKeys={[
                        CustomMessageConfigKey.BackMergeBranchPRTitle,
                        CustomMessageConfigKey.BackMergeBranchPRBody,
                        CustomMessageConfigKey.BackMergePRCommitPRTitle,
                        CustomMessageConfigKey.BackMergePRCommitPRBody,
                    ]}
                />
                <CustomMessagesSection
                    onEditClicked={onEditClicked}
                    customMessagesConfig={customMessagesConfig}
                    description="Production release commit tag at the end of each release cycle"
                    customMessageConfigKeys={[CustomMessageConfigKey.TagMessage]}
                />
                <CustomMessagesSection
                    onEditClicked={onEditClicked}
                    customMessagesConfig={customMessagesConfig}
                    description="Release summary generated when the release completes"
                    customMessageConfigKeys={[CustomMessageConfigKey.ReleaseSummaryMessage]}
                />
                <CustomMessagesSection
                    onEditClicked={onEditClicked}
                    customMessagesConfig={customMessagesConfig}
                    description="Summary of work in VCS Release artifact"
                    customMessageConfigKeys={[
                        CustomMessageConfigKey.VCSReleaseTitle,
                        CustomMessageConfigKey.VCSReleaseBody,
                    ]}
                />
                <CustomMessagesSection
                    onEditClicked={onEditClicked}
                    customMessagesConfig={customMessagesConfig}
                    description="Cherry-pick fixes from working branch to release branch and during hotfix creation"
                    customMessageConfigKeys={[
                        CustomMessageConfigKey.CherryPickPRTitle,
                        CustomMessageConfigKey.CherryPickPRBody,
                        CustomMessageConfigKey.CherryPickCommitMessage,
                    ]}
                />
            </Space>
            <CustomMessageModal
                appId={appId}
                orgId={orgId}
                customMessagesConfig={customMessagesConfig}
                open={showCustomMessageModal}
                onCancel={() => setShowCustomMessageModal(false)}
                customMessageConfigKey={currentCustomMessageKey}
                afterClose={() => setCurrentCustomMessageKey(null)}
            />
        </>
    );
};
